import { memo, useCallback, useState } from "react";
import {
  BaseEdge,
  EdgeLabelRenderer,
  getBezierPath,
  getSmoothStepPath,
  getStraightPath,
  useReactFlow,
} from "@xyflow/react";
import { Box, useTheme } from "@mui/material";
import styles from "./styles";

// Converte um trecho da spline Catmull-Rom (uniforme) nos dois pontos de controle da bezier
// cúbica equivalente, o que garante que a curva passe exatamente por p1 e p2 (ao contrário de uma
// bezier comum, cujo ponto de controle não fica sobre a linha)
const catmullRomToBezier = (p0, p1, p2, p3) => ({
  cp1x: p1.x + (p2.x - p0.x) / 6,
  cp1y: p1.y + (p2.y - p0.y) / 6,
  cp2x: p2.x - (p3.x - p1.x) / 6,
  cp2y: p2.y - (p3.y - p1.y) / 6,
});

// Aresta customizada com múltiplos pontos de curvatura arrastáveis entre equipamentos
function EquipmentEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  sourcePosition,
  targetPosition,
  style,
  label,
  labelStyle,
  labelBgStyle,
  labelBgPadding,
  labelBgBorderRadius,
  selected,
  data,
}) {
  const theme = useTheme();
  const edgeStyles = styles(theme);
  const { screenToFlowPosition } = useReactFlow();
  const [draggingIndex, setDraggingIndex] = useState(null);

  // Só existe callback quando o diagrama está em modo de edição (ver DiagramaCanvas)
  const onControlPointsChange = data?.onControlPointsChange;
  const editable = typeof onControlPointsChange === "function";

  // Lê o modo de linha salvo na aresta (default curva para compat com diagrams sem o campo)
  const lineMode = data?.lineMode || "curva";
  const controlPoints = Array.isArray(data?.controlPoints) ? data.controlPoints : [];
  // Pontos de curvatura funcionam em curva (spline) e reta (polilinha); ortogonal não expõe pontos
  const hasBend = (lineMode === "curva" || lineMode === "reta") && controlPoints.length > 0;

  let path = "";
  let labelX = 0;
  let labelY = 0;
  let hitSegments = [{ d: "" }];

  // Modos reta/ortogonal usam helpers nativos do React Flow; curva mantém a spline Catmull-Rom
  if (lineMode === "reta") {
    if (hasBend) {
      // Reta com pontos vira uma polilinha: segmentos retos entre origem, pontos arrastados e destino
      const anchors = [
        { x: sourceX, y: sourceY },
        ...controlPoints,
        { x: targetX, y: targetY },
      ];
      path = `M ${anchors[0].x} ${anchors[0].y} ${anchors
        .slice(1)
        .map((ponto) => `L ${ponto.x} ${ponto.y}`)
        .join(" ")}`;

      // Rótulo fica no ponto do meio da lista de pontos arrastados, igual ao modo curva
      const labelPoint = controlPoints[Math.floor((controlPoints.length - 1) / 2)];
      labelX = labelPoint.x;
      labelY = labelPoint.y;

      hitSegments = [];
      for (let i = 0; i < anchors.length - 1; i += 1) {
        hitSegments.push({
          d: `M ${anchors[i].x} ${anchors[i].y} L ${anchors[i + 1].x} ${anchors[i + 1].y}`,
        });
      }
    } else {
      [path, labelX, labelY] = getStraightPath({
        sourceX,
        sourceY,
        targetX,
        targetY,
      });
      hitSegments = [{ d: path }];
    }
  } else if (lineMode === "ortogonal") {
    // Cantos vivos: smoothstep com borderRadius 0 (RF não exporta getStepPath)
    [path, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: 0,
    });
    hitSegments = [{ d: path }];
  } else if (lineMode === "ortogonal-suave") {
    [path, labelX, labelY] = getSmoothStepPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
      borderRadius: 8,
    });
    hitSegments = [{ d: path }];
  } else {
    // Modo curva (default): mantém a lógica Catmull-Rom por pontos arrastáveis
    const [defaultPath, defaultLabelX, defaultLabelY] = getBezierPath({
      sourceX,
      sourceY,
      sourcePosition,
      targetX,
      targetY,
      targetPosition,
    });

    // Âncoras da curva: origem, pontos arrastados em ordem e destino
    const anchors = [
      { x: sourceX, y: sourceY },
      ...controlPoints,
      { x: targetX, y: targetY },
    ];

    // Um segmento de bezier cúbica por trecho da spline, unidos formam uma curva suave que passa
    // exatamente por todos os pontos arrastados
    const segmentCurves = [];
    for (let i = 0; i < anchors.length - 1; i += 1) {
      const p0 = anchors[Math.max(i - 1, 0)];
      const p1 = anchors[i];
      const p2 = anchors[i + 1];
      const p3 = anchors[Math.min(i + 2, anchors.length - 1)];
      const { cp1x, cp1y, cp2x, cp2y } = catmullRomToBezier(p0, p1, p2, p3);
      segmentCurves.push({
        p2,
        cp1x,
        cp1y,
        cp2x,
        cp2y,
        d: `M ${p1.x} ${p1.y} C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`,
      });
    }

    path = hasBend
      ? `M ${anchors[0].x} ${anchors[0].y} ${segmentCurves
          .map((s) => `C ${s.cp1x} ${s.cp1y}, ${s.cp2x} ${s.cp2y}, ${s.p2.x} ${s.p2.y}`)
          .join(" ")}`
      : defaultPath;

    // Rótulo fica no ponto do meio da lista de pontos arrastados, ou no meio da curva padrão
    const labelPoint = hasBend
      ? controlPoints[Math.floor((controlPoints.length - 1) / 2)]
      : { x: defaultLabelX, y: defaultLabelY };
    labelX = labelPoint.x;
    labelY = labelPoint.y;

    hitSegments = hasBend ? segmentCurves : [{ d: defaultPath }];
  }

  // Clicar em um trecho da linha já selecionada insere um novo ponto de curvatura naquele local
  const handleSegmentClick = useCallback(
    (evento, segmentIndex) => {
      if (!editable) return;
      evento.stopPropagation();
      const novosPontos = [...controlPoints];
      novosPontos.splice(
        segmentIndex,
        0,
        screenToFlowPosition({ x: evento.clientX, y: evento.clientY }),
      );
      onControlPointsChange(id, novosPontos);
    },
    [controlPoints, editable, id, onControlPointsChange, screenToFlowPosition],
  );

  const handlePointPointerDown = useCallback(
    (evento, pointIndex) => {
      if (!editable) return;
      evento.stopPropagation();
      evento.currentTarget.setPointerCapture?.(evento.pointerId);
      setDraggingIndex(pointIndex);
    },
    [editable],
  );

  const handlePointPointerMove = useCallback(
    (evento, pointIndex) => {
      if (draggingIndex !== pointIndex) return;
      const novaPosicao = screenToFlowPosition({
        x: evento.clientX,
        y: evento.clientY,
      });
      onControlPointsChange(
        id,
        controlPoints.map((ponto, idx) =>
          idx === pointIndex ? novaPosicao : ponto,
        ),
      );
    },
    [controlPoints, draggingIndex, id, onControlPointsChange, screenToFlowPosition],
  );

  const stopDragging = useCallback((evento) => {
    evento?.currentTarget?.releasePointerCapture?.(evento.pointerId);
    setDraggingIndex(null);
  }, []);

  // Duplo clique ou clique direito removem apenas o ponto clicado. preventDefault suprime o menu
  // de contexto nativo do navegador (clique direito) e também evita que o clique propague até o
  // onEdgeContextMenu da própria conexão (abriria o menu de excluir/cor por cima ao mesmo tempo)
  const handleRemovePoint = useCallback(
    (evento, pointIndex) => {
      if (!editable) return;
      evento.preventDefault?.();
      evento.stopPropagation();
      onControlPointsChange(
        id,
        controlPoints.filter((_, idx) => idx !== pointIndex),
      );
    },
    [controlPoints, editable, id, onControlPointsChange],
  );

  return (
    <>
      <BaseEdge
        id={id}
        path={path}
        style={style}
        label={label}
        labelX={labelX}
        labelY={labelY}
        labelStyle={labelStyle}
        labelBgStyle={labelBgStyle}
        labelBgPadding={labelBgPadding}
        labelBgBorderRadius={labelBgBorderRadius}
      />
      {editable && selected && (lineMode === "curva" || lineMode === "reta") && (
        <>
          {hitSegments.map((segmento, indice) => (
            <path
              key={indice}
              d={segmento.d}
              style={edgeStyles.segmentHitArea}
              onClick={(evento) => handleSegmentClick(evento, indice)}
            />
          ))}
          <EdgeLabelRenderer>
            {controlPoints.map((ponto, indice) => (
              <Box
                key={indice}
                className="nodrag nopan"
                onPointerDown={(evento) => handlePointPointerDown(evento, indice)}
                onPointerMove={(evento) => handlePointPointerMove(evento, indice)}
                onPointerUp={stopDragging}
                onPointerCancel={stopDragging}
                onDoubleClick={(evento) => handleRemovePoint(evento, indice)}
                onContextMenu={(evento) => handleRemovePoint(evento, indice)}
                sx={{
                  ...edgeStyles.bendHandle,
                  transform: `translate(-50%, -50%) translate(${ponto.x}px, ${ponto.y}px)`,
                  cursor: draggingIndex === indice ? "grabbing" : "grab",
                }}
              />
            ))}
          </EdgeLabelRenderer>
        </>
      )}
    </>
  );
}

// Registro dos tipos de aresta do React Flow, declarado fora do componente para evitar recriação a cada render
export const edgeTypes = { equipamento: EquipmentEdge };

export default memo(EquipmentEdge);
